import React, { useState, useEffect } from 'react';
import { SPM8_SEA_LEVEL, SPM_FIGURE_METADATA } from '../../data/ipccData';
import { AudioEngine } from '../../audio/AudioEngine';
import { FigureDescriptionPanel } from '../FigureDescriptionPanel';
import { Shield, Play, Pause, Waves, AlertTriangle } from 'lucide-react';

interface SPM8SeaLevelProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
}

export const SPM8SeaLevel: React.FC<SPM8SeaLevelProps> = ({ isPlaying, onTogglePlay }) => {
  const meta = SPM_FIGURE_METADATA['spm8'];
  const audioEngine = AudioEngine.getInstance();

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [showHighImpact, setShowHighImpact] = useState<boolean>(true);

  // Auto-play timeline loop
  useEffect(() => {
    let timer: number;
    if (isPlaying) {
      timer = window.setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % SPM8_SEA_LEVEL.length);
      }, 1000);
    } else {
      audioEngine.stopAbyssalDrone();
    }
    return () => clearInterval(timer);
  }, [isPlaying]);

  // Audio Update per step
  useEffect(() => {
    if (!audioEngine.getIsUnlocked()) return;
    const pt = SPM8_SEA_LEVEL[currentIndex];
    if (!pt) return;

    if (isPlaying) {
      // Sea level magnitude (meters) maps to sub-bass drone pitch (20Hz to 80Hz)
      const seaLevelMeters = showHighImpact ? pt.ssp5_highImpact : pt.ssp5_85;
      const droneFreq = 20 + Math.min(60, seaLevelMeters * 5);

      audioEngine.startAbyssalDrone(droneFreq, 0.45);
    } else {
      audioEngine.stopAbyssalDrone();
    }
  }, [currentIndex, isPlaying, showHighImpact]);

  const activePt = SPM8_SEA_LEVEL[currentIndex];

  return (
    <div className="space-y-6">
      {/* Title Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 glass-panel p-5 rounded-2xl border border-slate-800">
        <div>
          <span className="text-xs font-mono text-cyan-400 font-semibold">{meta.number}</span>
          <h2 className="text-xl font-extrabold text-white">{meta.title}</h2>
          <p className="text-xs text-slate-400 mt-1">{meta.subtitle}</p>
        </div>

        <button
          onClick={onTogglePlay}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-lg ${
            isPlaying ? 'bg-amber-500 text-slate-950 glow-amber' : 'bg-cyan-500 text-slate-950 glow-cyan'
          }`}
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          <span>{isPlaying ? 'Pause Sub-Bass Drone' : 'Play Abyssal Drone (20-80Hz)'}</span>
        </button>
      </div>

      {/* Main Sea Level Commitment Module */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-800 space-y-6">
        {/* Toggle Low-Likelihood High-Impact Scenario */}
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-2xl bg-slate-900/90 border border-slate-800">
          <div className="flex items-center gap-2.5">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div>
              <span className="text-xs font-bold text-white block">Low-Likelihood High-Impact Ice Sheet Instability</span>
              <span className="text-[11px] text-slate-400">Includes Antarctic marine ice cliff instability (MICI) to year 2300</span>
            </div>
          </div>

          <button
            onClick={() => setShowHighImpact(!showHighImpact)}
            className={`px-4 py-2 rounded-xl text-xs font-bold font-mono transition-all border ${
              showHighImpact
                ? 'bg-red-950 text-red-300 border-red-500/60 glow-red'
                : 'bg-slate-950 text-slate-400 border-slate-800'
            }`}
          >
            {showHighImpact ? 'High-Impact Included (+15m)' : 'Standard SSP Projections'}
          </button>
        </div>

        {/* Timeline Scrubber */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs font-mono text-slate-400">
            <span>Year 2000</span>
            <span className="text-cyan-300 font-bold">Selected Horizon: Year {activePt.year}</span>
            <span>Year 2300</span>
          </div>
          <input
            type="range"
            min="0"
            max={SPM8_SEA_LEVEL.length - 1}
            value={currentIndex}
            onChange={(e) => setCurrentIndex(parseInt(e.target.value))}
            className="w-full h-2 rounded-lg bg-slate-800 accent-cyan-400 cursor-pointer"
          />
        </div>

        {/* Scenario Sea Level Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-1">
            <span className="text-xs font-mono text-cyan-400 font-semibold block">SSP1-2.6 (Low Emissions)</span>
            <span className="text-2xl font-extrabold font-mono text-white">+{activePt.ssp1_26.toFixed(2)} m</span>
            <span className="text-[10px] text-slate-500 block">Relative to 1900</span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-1">
            <span className="text-xs font-mono text-amber-400 font-semibold block">SSP2-4.5 (Intermediate)</span>
            <span className="text-2xl font-extrabold font-mono text-white">+{activePt.ssp2_45.toFixed(2)} m</span>
            <span className="text-[10px] text-slate-500 block">Relative to 1900</span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-1">
            <span className="text-xs font-mono text-orange-400 font-semibold block">SSP5-8.5 (Very High)</span>
            <span className="text-2xl font-extrabold font-mono text-white">+{activePt.ssp5_85.toFixed(2)} m</span>
            <span className="text-[10px] text-slate-500 block">Relative to 1900</span>
          </div>

          <div className={`p-4 rounded-2xl border space-y-1 ${showHighImpact ? 'bg-red-950/40 border-red-500/50' : 'bg-slate-900/80 border-slate-800 opacity-60'}`}>
            <span className="text-xs font-mono text-red-400 font-semibold block">Low-Likelihood High-Impact</span>
            <span className="text-2xl font-extrabold font-mono text-red-300">+{activePt.ssp5_highImpact.toFixed(2)} m</span>
            <span className="text-[10px] text-slate-400 block">Extreme Ice Sheet Surge</span>
          </div>
        </div>
      </div>

      {/* Scientific Synopsis & Original Figure Reference Panel */}
      <FigureDescriptionPanel meta={meta} />
    </div>
  );
};
